import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Add, ExpandMore } from '@mui/icons-material';
import { createAdminFormSubmission } from '../../services/firebaseService';
import SkillsAutocomplete from '../landing/SkillsAutocomplete';
import { fieldsOfStudy } from '../../data/fieldsOfStudy';

const emptyForm = {
  name: '',
  phoneNumber: '',
  birthYear: '',
  emiratesOfResidence: '',
  referredBy: '',
  linkedinAccount: '',
  email: '',
  educationLevel: '',
  fieldOfStudy: '',
  otherFieldOfStudy: '',
  coursesCompleted: '',
  universityName: '',
  currentPosition: '',
  companyName: '',
  lookingForJob: false,
  professionalJourney: '',
  communityHelpedEmployment: '',
  skills: [] as string[],
  interestedInSpeaking: '',
  speakingTopics: '',
  interestedInEntrepreneurship: '',
  hasProjectIdea: '',
  projectIdeaDescription: '',
  interestedInVolunteering: '',
  volunteerTeam: '',
  generalNote: '',
};

interface AddCommunityMemberFormProps {
  onAdded: () => void;
}

const AddCommunityMemberForm: React.FC<AddCommunityMemberFormProps> = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<{ name?: string; phoneNumber?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const birthYears = Array.from(
    { length: new Date().getFullYear() - 18 - 1949 },
    (_, i) => (new Date().getFullYear() - 18 - i).toString()
  );

  const handleChange = (field: keyof typeof emptyForm, value: string | string[] | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'name' && errors.name) setErrors(prev => ({ ...prev, name: undefined }));
    if (field === 'phoneNumber' && errors.phoneNumber) {
      setErrors(prev => ({ ...prev, phoneNumber: undefined }));
    }
  };

  const validate = () => {
    const newErrors: { name?: string; phoneNumber?: string } = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[+]?[1-9][\d]{0,15}$/.test(form.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
    setSubmitError('');
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      await createAdminFormSubmission(form);
      setOpen(false);
      resetForm();
      onAdded();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to add person');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<Add />}
        onClick={() => setOpen(true)}
        sx={{ mb: 2 }}
      >
        Add Person
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Add Community Member</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Name and phone number are required. All other fields are optional.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <TextField
                label="Full Name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                sx={{ flex: '1 1 240px' }}
              />
              <TextField
                label="Phone Number"
                value={form.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                required
                sx={{ flex: '1 1 240px' }}
              />
            </Box>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Personal Information (optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <FormControl sx={{ flex: '1 1 200px' }}>
                      <InputLabel>Birth Year</InputLabel>
                      <Select
                        value={form.birthYear}
                        label="Birth Year"
                        onChange={(e) => handleChange('birthYear', e.target.value)}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {birthYears.map(year => (
                          <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: '1 1 200px' }}>
                      <InputLabel>Emirates of Residence</InputLabel>
                      <Select
                        value={form.emiratesOfResidence}
                        label="Emirates of Residence"
                        onChange={(e) => handleChange('emiratesOfResidence', e.target.value)}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'].map(emirate => (
                          <MenuItem key={emirate} value={emirate}>{emirate}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <TextField
                    label="Referred By"
                    value={form.referredBy}
                    onChange={(e) => handleChange('referredBy', e.target.value)}
                    fullWidth
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      sx={{ flex: '1 1 240px' }}
                    />
                    <TextField
                      label="LinkedIn Profile URL"
                      value={form.linkedinAccount}
                      onChange={(e) => handleChange('linkedinAccount', e.target.value)}
                      sx={{ flex: '1 1 240px' }}
                    />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Academic Information (optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Education Level</InputLabel>
                    <Select
                      value={form.educationLevel}
                      label="Education Level"
                      onChange={(e) => handleChange('educationLevel', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {['High School', 'Diploma', "Bachelor's Degree", "Master's Degree", 'PhD', 'Other'].map(level => (
                        <MenuItem key={level} value={level}>{level}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Field of Study</InputLabel>
                    <Select
                      value={form.fieldOfStudy}
                      label="Field of Study"
                      onChange={(e) => handleChange('fieldOfStudy', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {fieldsOfStudy.map(field => (
                        <MenuItem key={field} value={field}>{field}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {form.fieldOfStudy === 'Other' && (
                    <TextField
                      label="Other Field of Study"
                      value={form.otherFieldOfStudy}
                      onChange={(e) => handleChange('otherFieldOfStudy', e.target.value)}
                      fullWidth
                    />
                  )}
                  <TextField
                    label="University / Institute"
                    value={form.universityName}
                    onChange={(e) => handleChange('universityName', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Courses Completed"
                    value={form.coursesCompleted}
                    onChange={(e) => handleChange('coursesCompleted', e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Professional Information (optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                      label="Current Position"
                      value={form.currentPosition}
                      onChange={(e) => handleChange('currentPosition', e.target.value)}
                      sx={{ flex: '1 1 240px' }}
                    />
                    <TextField
                      label="Company Name"
                      value={form.companyName}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      sx={{ flex: '1 1 240px' }}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.lookingForJob}
                        onChange={(e) => handleChange('lookingForJob', e.target.checked)}
                      />
                    }
                    label="Looking for a job"
                  />
                  <TextField
                    label="Professional Journey"
                    value={form.professionalJourney}
                    onChange={(e) => handleChange('professionalJourney', e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Community helped with income/employment?</InputLabel>
                    <Select
                      value={form.communityHelpedEmployment}
                      label="Community helped with income/employment?"
                      onChange={(e) => handleChange('communityHelpedEmployment', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                  <SkillsAutocomplete
                    value={form.skills}
                    onChange={(skills) => handleChange('skills', skills)}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Additional Information (optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Interested in speaking?</InputLabel>
                    <Select
                      value={form.interestedInSpeaking}
                      label="Interested in speaking?"
                      onChange={(e) => handleChange('interestedInSpeaking', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                  {form.interestedInSpeaking === 'Yes' && (
                    <TextField
                      label="Speaking Topics"
                      value={form.speakingTopics}
                      onChange={(e) => handleChange('speakingTopics', e.target.value)}
                      fullWidth
                    />
                  )}
                  <FormControl fullWidth>
                    <InputLabel>Interested in entrepreneurship?</InputLabel>
                    <Select
                      value={form.interestedInEntrepreneurship}
                      label="Interested in entrepreneurship?"
                      onChange={(e) => handleChange('interestedInEntrepreneurship', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Has project/idea requiring support?</InputLabel>
                    <Select
                      value={form.hasProjectIdea}
                      label="Has project/idea requiring support?"
                      onChange={(e) => handleChange('hasProjectIdea', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                  {form.hasProjectIdea === 'Yes' && (
                    <TextField
                      label="Project/Idea Description"
                      value={form.projectIdeaDescription}
                      onChange={(e) => handleChange('projectIdeaDescription', e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  )}
                  <FormControl fullWidth>
                    <InputLabel>Interested in volunteering?</InputLabel>
                    <Select
                      value={form.interestedInVolunteering}
                      label="Interested in volunteering?"
                      onChange={(e) => handleChange('interestedInVolunteering', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                  {form.interestedInVolunteering === 'Yes' && (
                    <FormControl fullWidth>
                      <InputLabel>Volunteer Team</InputLabel>
                      <Select
                        value={form.volunteerTeam}
                        label="Volunteer Team"
                        onChange={(e) => handleChange('volunteerTeam', e.target.value)}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {['Events', 'Content', 'Mentorship', 'Technical', 'Marketing', 'Other'].map(team => (
                          <MenuItem key={team} value={team}>{team}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <TextField
                    label="General Note"
                    value={form.generalNote}
                    onChange={(e) => handleChange('generalNote', e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Person'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default AddCommunityMemberForm;
